import React, { useEffect, useState } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Typography, 
  TextField, 
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { recupereUtilisateurs, supprimerUtilisateur } from '../Services';
import { Utilisateur } from '../models/Utilisateur';
import Home from './Home';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const UtilisateursScreen = () => {
  const [utilisateurs, setUtilisateurs] = useState<Utilisateur[]>([]);
  const [filteredUtilisateurs, setFilteredUtilisateurs] = useState<Utilisateur[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<Utilisateur | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const fetchUtilisateurs = async () => {
    try {
      const users = await recupereUtilisateurs();
      setUtilisateurs(users);
      setFilteredUtilisateurs(users);
    } catch (error) {
      console.error('Erreur lors de la récupération des utilisateurs:', error);
    }
  };

  useEffect(() => {
    fetchUtilisateurs();
  }, []);

  useEffect(() => {
    // Filtrer les utilisateurs en fonction du terme de recherche
    const filtered = utilisateurs.filter((user) =>
      user.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.telephone.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUtilisateurs(filtered);
  }, [searchTerm, utilisateurs]);

  // Fonction pour exporter les utilisateurs filtrés au format Excel
  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredUtilisateurs);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Utilisateurs');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/octet-stream' });

    saveAs(data, 'utilisateurs.xlsx');
  };

  // Fonction pour ouvrir le dialogue de confirmation
  const handleOpenDialog = (user: Utilisateur) => {
    setSelectedUser(user);
    setOpenDialog(true);
  };

  // Fonction pour fermer le dialogue
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
  };

  // Fonction pour supprimer l'utilisateur
  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      await supprimerUtilisateur(selectedUser.id_utilisateur);
      
      // Recharger toutes les données depuis le serveur
      await fetchUtilisateurs();
      
      setSnackbarMessage(`L'utilisateur ${selectedUser.nom} a été supprimé avec succès.`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'utilisateur:', error);
      setSnackbarMessage('Erreur lors de la suppression de l\'utilisateur.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      
      // Recharger les données en cas d'erreur pour s'assurer que la liste est synchronisée
      await fetchUtilisateurs();
    }

    handleCloseDialog();
  };

  // Fonction pour fermer le snackbar
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <>
      <Home />
      <Typography variant="h6" style={{ margin: '20px 0', marginLeft: '15px' }}>
        Nombre de Clients: {filteredUtilisateurs.length}
      </Typography>
      
      {/* Champ de recherche */}
      <TextField
        label="Rechercher par Nom, Email ou Téléphone"
        variant="outlined"
        style={{ marginBottom: '20px', marginLeft: '20px', width: '50%' }}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      {/* Bouton d'exportation */}
      <Button
        variant="contained"
        color="primary"
        onClick={exportToExcel}
        style={{ marginLeft: '20px', marginBottom: '20px' }}
      >
        Exporter en Excel
      </Button>

      <TableContainer component={Paper} style={{ maxHeight: '800px' }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Nom</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Mdp</TableCell>
              <TableCell>Téléphone</TableCell>
              <TableCell>Date d'inscription</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUtilisateurs.map((user) => (
              <TableRow key={user.id_utilisateur}>
                <TableCell>{user.id_utilisateur}</TableCell>
                <TableCell>{user.nom}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.mot_de_passe}</TableCell>
                <TableCell>{user.telephone}</TableCell>
                <TableCell>{new Date(user.date_inscription).toLocaleDateString()}</TableCell>
                <TableCell align="center">
                  <IconButton
                    color="error"
                    onClick={() => handleOpenDialog(user)}
                    title="Supprimer l'utilisateur"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialogue de confirmation */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Confirmer la suppression
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Êtes-vous sûr de vouloir supprimer l'utilisateur {selectedUser?.nom} ({selectedUser?.email}) ?
            <br />
            <strong>Attention :</strong> Cette action supprimera également toutes les données associées 
            (demandes, voyageurs, etc.) et ne peut pas être annulée.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Annuler
          </Button>
          <Button onClick={handleDeleteUser} color="error" autoFocus>
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar pour les notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UtilisateursScreen;